import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { basketPricingQueryKey } from './useBasketPricing';
import { basketPricingInputFrom, type QuotePricingSource } from '../utils/pricedLines';
import { decimalToMinorUnits } from '../utils/decimalMoney';
import { priceChangedBlock } from '../utils/pricingErrors';
import { samePricingInput, type PricingOverride } from '../services/quoteSaveLane';
import { PaymentAmountStaleError } from '../services/quoteSaveErrors';
import type { BasketPricingInput, PricedBasket } from '../types/basketPricing.types';

export interface PriceChangeConfirmationState {
    readonly isOpen: boolean;
    readonly previousTotalMinor: number | null;
    readonly freshTotalMinor: number | null;
}

const CLOSED_STATE: PriceChangeConfirmationState = {
    isOpen: false,
    previousTotalMinor: null,
    freshTotalMinor: null,
};

export type ConfirmedPriceSave = (override: PricingOverride) => Promise<unknown>;

export interface UsePriceChangeConfirmationResult {
    readonly state: PriceChangeConfirmationState;
    readonly isPending: boolean;
    readonly open: (fresh: PricedBasket, refusedInput?: BasketPricingInput | null) => void;
    readonly close: () => void;
    readonly confirm: (save: ConfirmedPriceSave) => Promise<void>;
}

/** Holds the server's fresh figures from a `price_changed` refusal and hands that exact block to
 *  the confirmed save; the cache is written once the server has accepted those figures. */
export type ReadPricedQuote = () => QuotePricingSource & { readonly draftId?: string };

/** The amount check runs after pricing verification, so this refusal means the confirmed
 *  figures were accepted and only the payment amount still needs reconfirming. */
const isAmountRefusalOfConfirmed = (error: unknown, confirmed: PricingOverride): boolean =>
    error instanceof PaymentAmountStaleError &&
    error.grossMinor === decimalToMinorUnits(confirmed.pricing.totals.gross);

export const usePriceChangeConfirmation = (
    readQuote: ReadPricedQuote,
): UsePriceChangeConfirmationResult => {
    const queryClient = useQueryClient();
    const [state, setState] = useState<PriceChangeConfirmationState>(CLOSED_STATE);
    const [confirmed, setConfirmed] = useState<PricingOverride | null>(null);
    const [isPending, setIsPending] = useState(false);

    const open = useCallback(
        (fresh: PricedBasket, refusedInput?: BasketPricingInput | null) => {
            const input = basketPricingInputFrom(readQuote());
            if (!input) return;
            if (refusedInput !== undefined && !samePricingInput(refusedInput, input)) return;
            // Read-only: figures the user has not consented to must never reach the cache
            // `saveQuote` reads back, where they would be persisted without a confirmation.
            const previous = queryClient.getQueryState<PricedBasket>(basketPricingQueryKey(input));
            setConfirmed({ pricing: fresh, input });
            setState({
                isOpen: true,
                previousTotalMinor: previous?.data ? decimalToMinorUnits(previous.data.totals.gross) : null,
                freshTotalMinor: decimalToMinorUnits(fresh.totals.gross),
            });
        },
        [readQuote, queryClient],
    );

    const close = useCallback(() => {
        setConfirmed(null);
        setState(CLOSED_STATE);
    }, []);

    const confirm = useCallback(
        async (save: ConfirmedPriceSave) => {
            if (confirmed === null || isPending) return;
            setIsPending(true);
            const { draftId } = readQuote();
            // The save drops an override whose basket has moved, so the figures may be cached
            // only while the draft and basket are the ones confirmed.
            const cacheConfirmed = () => {
                const current = readQuote();
                if (
                    confirmed.input &&
                    current.draftId === draftId &&
                    samePricingInput(confirmed.input, basketPricingInputFrom(current))
                ) {
                    queryClient.setQueryData(basketPricingQueryKey(confirmed.input), confirmed.pricing);
                }
            };
            try {
                await save(confirmed);
                cacheConfirmed();
                close();
            } catch (error) {
                if (isAmountRefusalOfConfirmed(error, confirmed)) cacheConfirmed();
                // A second refusal has already reopened this dialog on the newer figures; any
                // other failure is surfaced by the save lane, so release the dialog for it.
                if (priceChangedBlock(error) === null) close();
            } finally {
                setIsPending(false);
            }
        },
        [confirmed, isPending, readQuote, queryClient, close],
    );

    return { state, isPending, open, close, confirm };
};
